import { useRouter } from 'next/router'
import { useEffect } from 'react'

/**
 * Captura a origem (`?ref=` e utm_*) do link que trouxe o visitante e guarda em
 * `sessionStorage` para o cadastro/avaliação consumir depois.
 *
 * Escopo da issue #822: **apenas propagação**. A persistência no cadastro e os
 * eventos de funil ficam na issue #821. First-touch: só grava se ainda não
 * houver origem na sessão.
 *
 * A leitura é client-side de propósito — as páginas de perfil são estáticas
 * (ISR) e não devem variar por query string no cache.
 */
export const ATTRIBUTION_KEY = 'goodfellas:attribution'

export interface Attribution {
  ref?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  capturedAt: string
}

export function useAttribution(): void {
  const router = useRouter()

  useEffect(() => {
    if (!router.isReady) return
    if (typeof window === 'undefined') return

    const { ref, utm_source, utm_medium, utm_campaign } = router.query
    const first = (v: string | string[] | undefined): string | undefined =>
      Array.isArray(v) ? v[0] : v

    const attribution: Omit<Attribution, 'capturedAt'> = {
      ref: first(ref),
      utmSource: first(utm_source),
      utmMedium: first(utm_medium),
      utmCampaign: first(utm_campaign),
    }

    const hasAny = Object.values(attribution).some(Boolean)
    if (!hasAny) return

    try {
      // First-touch: não sobrescreve uma origem já capturada nesta sessão.
      if (sessionStorage.getItem(ATTRIBUTION_KEY)) return
      sessionStorage.setItem(
        ATTRIBUTION_KEY,
        JSON.stringify({
          ...attribution,
          capturedAt: new Date().toISOString(),
        }),
      )
    } catch {
      // sessionStorage indisponível (modo privado etc.) — ignora.
    }
  }, [router.isReady, router.query])
}

/** Lê a origem capturada nesta sessão (para a #821 consumir no cadastro). */
export function readAttribution(): Attribution | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_KEY)
    return raw ? (JSON.parse(raw) as Attribution) : null
  } catch {
    return null
  }
}
